import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Chip
} from '@mui/material';
import { Settings as SettingsIcon, BusinessCenter as BranchIcon } from '@mui/icons-material';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const PreferredBranchSettings = () => {
  const { user, setUser } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(user?.preferred_branch || '');
  const [loading, setLoading] = useState(false);
  const [fetchingBranches, setFetchingBranches] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    if (user?.preferred_branch) {
      setSelectedBranch(user.preferred_branch);
    }
  }, [user]);

  const fetchBranches = async () => {
    try {
      setFetchingBranches(true);
      const response = await apiService.getBranches();
      setBranches(response);
    } catch (err) {
      setError('Failed to fetch branches');
      console.error('Error fetching branches:', err);
    } finally {
      setFetchingBranches(false);
    }
  };

  const handleBranchChange = (event) => {
    setSelectedBranch(event.target.value);
    setError(null);
    setSuccessMessage('');
  };

  const handleSubmit = async () => {
    if (!selectedBranch) {
      setError('Please select a branch');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.updateMyPreferredBranch(selectedBranch);
      
      // Update the user context with the new user data
      if (response.user) {
        setUser(response.user);
      }
      
      setSuccessMessage(`Preferred branch updated to ${selectedBranch}. This will be your active branch for managing operations.`);
    } catch (err) {
      setError(err || 'Failed to update preferred branch');
      console.error('Error updating preferred branch:', err);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingBranches) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, margin: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h5">Preferred Branch Settings</Typography>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary" paragraph>
              As an admin user, you can select which branch you want to actively manage. 
              This determines which branch's data you'll see and manage in the system.
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                <strong>Your assigned branch:</strong>
              </Typography>
              <Chip 
                icon={<BranchIcon />}
                label={user?.branch || 'Not assigned'} 
                color="default" 
                variant="outlined" 
                size="small"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                <strong>Currently active branch:</strong>
              </Typography>
              <Chip 
                icon={<BranchIcon />}
                label={user?.active_branch || user?.preferred_branch || 'None selected'} 
                color="primary" 
                size="small"
              />
            </Box>
          </Box>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Preferred Branch</InputLabel>
            <Select
              value={selectedBranch}
              label="Select Preferred Branch"
              onChange={handleBranchChange}
              disabled={loading}
            >
              {branches.map(branch => (
                <MenuItem key={branch.name} value={branch.name}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !selectedBranch || selectedBranch === user?.preferred_branch}
            sx={{ mb: 2 }}
            fullWidth
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Updating...
              </>
            ) : (
              'Update Preferred Branch'
            )}
          </Button>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          
          {successMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {successMessage}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PreferredBranchSettings; 